import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import {ImagePreview} from "../image-preview.tsx";

import {PREVIEW_HEIGHT, PREVIEW_WIDTH} from "./constants.ts";
import {sharedStyles} from "./shared-styles.ts";
import type {SharedModalContentProps} from "./types.ts";

import imageIcon from "@phosphor-icons/core/regular/image.svg";
import trashIcon from "@phosphor-icons/core/regular/trash-simple.svg";

/**
 * Renders the URL input experience for the image attachment modal.
 * Displays a text field for entering an image URL and a preview of the fetched image.
 * Used as a content component within the image attach modal wrapper.
 */
interface Props extends SharedModalContentProps {
    // The image URL for preview.
    imageURL: string;
    // Error message for the URL input, or null if none.
    error?: string | null;
    // The current value of the URL input field.
    pendingImageURL: string;
    // Callback to set the value of the URL input field.
    setPendingImageURL: (url: string) => void;
    // Callback to fetch and set the image from the URL.
    handleSetImageURL: () => void;
    // Callback to clear the fetched image.
    handleClearImage: () => void;
    // The name of the selected file, or null if none.
    selectedFilename: string | null;
}

export const UrlModalContent = ({
    image,
    imageURL,
    error,
    pendingImageURL,
    setPendingImageURL,
    handleSetImageURL,
    handleClearImage,
    selectedFilename,
}: Props) => (
    <>
        <View style={styles.urlContainer}>
            <TextField
                placeholder="https://khanacademy.org/algebra1/..."
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        handleSetImageURL();
                    }
                }}
                onBlur={() => null}
                value={pendingImageURL}
                onChange={setPendingImageURL}
            />
            <Strut size={12} />
            <Button
                disabled={!pendingImageURL}
                kind="secondary"
                onClick={handleSetImageURL}
                aria-label={t`Preview image`}
            >
                {t`Preview`}
            </Button>
        </View>
        {error ? (
            <LabelMedium style={styles.errorMessage}>{error}</LabelMedium>
        ) : (
            <Strut size={32} />
        )}
        <View style={sharedStyles.previewContainer}>
            {image && !error && image.imageURL ? (
                <ImagePreview
                    image={image.imageURL}
                    displayWidth={PREVIEW_WIDTH}
                    displayHeight={PREVIEW_HEIGHT}
                />
            ) : null}
        </View>
        <Strut size={12} />
        {image && (
            <View style={sharedStyles.imageFilenameContainer}>
                <View style={sharedStyles.imageFilenameRow}>
                    <PhosphorIcon icon={imageIcon} size="medium" />
                    <LabelMedium style={sharedStyles.imageFilenameOverflow}>
                        {selectedFilename}
                        {imageURL}
                    </LabelMedium>
                </View>
                <IconButton
                    icon={trashIcon}
                    kind="tertiary"
                    size="small"
                    onClick={handleClearImage}
                    aria-label={t`Clear image`}
                    testId="image-attach-clear-button"
                />
            </View>
        )}
    </>
);

const styles = StyleSheet.create({
    urlContainer: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
    },
    errorMessage: {
        color: color.red,
        paddingBlockStart: spacing.xxxSmall_4,
        paddingBlockEnd: spacing.xxxSmall_4,
        width: "100%",
    },
});
